// src/context/AuthContext.jsx
// One-time payment model — Starter / Popular / Lifetime
// Upgrade tier HANYA dibuat oleh Cloud Function (webhook Bayarcash).
// Client tidak dibenarkan tukar accessType — dikuatkuasa oleh Firestore rules.

import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, increment } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";

// ─── Tier config ────────────────────────────────────────────────────────────
// accessType  : 'free' | 'starter' | 'popular' | 'lifetime'
// promptsLimit: 3 (free) | 20 (starter) | 100 (popular) | 999999 (lifetime)
// Bila promptsUsed >= promptsLimit → kena beli/upgrade
// Lifetime → tak pernah habis

export const TIER_CONFIG = {
  free:     { label: "Free",     limit: 3,      price: 0 },
  starter:  { label: "Starter",  limit: 20,     price: 4.90 },
  popular:  { label: "Popular",  limit: 100,    price: 8.90 },
  lifetime: { label: "Lifetime", limit: 999999, price: 9.90 },
};

// Admin emails — unlimited access automatik
const ADMIN_EMAILS = ["imans9715@gmail.com"];

// ─── Context ─────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  // Derived state
  const isAdmin      = ADMIN_EMAILS.includes(user?.email);
  const accessType   = isAdmin ? "lifetime" : (userDoc?.accessType ?? "free");
  const promptsUsed  = userDoc?.promptsUsed ?? 0;
  const promptsLimit = TIER_CONFIG[accessType]?.limit ?? TIER_CONFIG.free.limit;
  const isLifetime   = accessType === "lifetime";
  const canUsePrompt = isLifetime || promptsUsed < promptsLimit;
  const promptsLeft  = isLifetime ? "∞" : Math.max(0, promptsLimit - promptsUsed);

  // ── Auth listener ──────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadOrCreateUserDoc(firebaseUser);
      } else {
        setUser(null);
        setUserDoc(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Load atau create Firestore doc ─────────────────────────────────────────
  async function loadOrCreateUserDoc(firebaseUser) {
    const ref  = doc(db, "users", firebaseUser.uid);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      setUserDoc(snap.data());
    } else {
      // User baru — WAJIB start free (dikuatkuasa oleh Firestore rules)
      const newDoc = {
        uid:          firebaseUser.uid,
        email:        firebaseUser.email,
        displayName:  firebaseUser.displayName,
        photoURL:     firebaseUser.photoURL,
        accessType:   "free",
        promptsUsed:  0,
        promptsLimit: TIER_CONFIG.free.limit,
        createdAt:    serverTimestamp(),
        // Fields ini akan diisi oleh Cloud Function bila user beli:
        // bclOrderId, purchasedAt, purchasedTier
      };
      await setDoc(ref, newDoc);
      setUserDoc(newDoc);
    }
  }

  // ── Increment prompt usage ─────────────────────────────────────────────────
  async function incrementPromptUsage() {
    if (!user) return;
    if (isLifetime) return; // Lifetime/admin tak perlu track

    const ref = doc(db, "users", user.uid);
    const newCount = promptsUsed + 1;

    await updateDoc(ref, { promptsUsed: increment(1) });
    setUserDoc(prev => ({ ...prev, promptsUsed: newCount }));
  }

  // ── Google Login / Logout ──────────────────────────────────────────────────
  async function loginWithGoogle() {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error("Login error:", err);
    }
  }

  async function logout() {
    await signOut(auth);
  }

  // ─────────────────────────────────────────────────────────────────────────
  const value = {
    user,
    userDoc,
    isAdmin,
    loading,
    accessType,
    promptsUsed,
    promptsLimit,
    promptsLeft,
    isLifetime,
    canUsePrompt,
    loginWithGoogle,
    logout,
    incrementPromptUsage,
  };

  if (loading) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}